import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { Segmented, Switch, Tooltip } from 'antd';
import { ColumnCustomizationDrawer } from 'components/Custom/ColumnCustomizationDrawer';
import { QuestionMarkTooltip } from 'components/Custom/QuestionMarkTooltip';
import {
  type Preference,
  PreferenceActions,
} from 'utils/preferences';
import {
  ENTITY_TRANSACTIONS_EDIT_IN_DRAWER_PREF,
  ENTITY_TRANSACTIONS_EDIT_IN_DRAWER_PREF_ALL_ENTITIES_KEY,
  TRANSACTIONS_TAB_COLUMNS_CONFIG_ALL_ENTITIES_KEY,
} from './constants';
import {
  transactionsTabColumnsConfigKeyForOrg,
  type TransactionsTabColumnsPersistConfig,
} from './preferences';

export type TransactionEditMode = 'drawer' | 'table';

/** Drawer ↔ table edit mode (review drawer toolbar + attachment side panel). */
export const TransactionEditModeSegmented: React.FC<{
  value: TransactionEditMode;
  onChange: (mode: TransactionEditMode) => void;
  disabled?: boolean;
}> = ({ value, onChange, disabled }) => {
  const { t } = useTranslation();
  return (
  <Segmented<TransactionEditMode>
    size="small"
    disabled={disabled}
    value={value}
    onChange={(mode) => onChange(mode)}
    options={[
      { label: t('organisation.transactions-settings-edit-mode-drawer'), value: 'drawer' },
      { label: t('organisation.transactions-settings-edit-mode-table'), value: 'table' },
    ]}
  />
  );
};

/** Persisted prefs (transactions tab toolbar) vs draft-only (settings drawer until Save). */
export const EntityTransactionsEditInTablePrefSwitch: React.FC<
  | {
      draftMode: true;
      draftEditInDrawer: boolean;
      onDraftEditInDrawerChange: (editInDrawer: boolean) => void;
      tooltipWhenEditInTable: string;
      tooltipWhenEditInDrawer: string;
    }
  | {
      draftMode?: false;
      organisationId: string;
      handlePreferences: (
        action: PreferenceActions,
        preference: Preference
      ) => unknown;
      onPersistedEditInDrawerChange: (editInDrawer: boolean) => void;
      tooltipWhenEditInTable: string;
      tooltipWhenEditInDrawer: string;
    }
> = (props) => {
  if (props.draftMode) {
    const {
      draftEditInDrawer,
      onDraftEditInDrawerChange,
      tooltipWhenEditInTable,
      tooltipWhenEditInDrawer,
    } = props;
    return (
      <Tooltip
        title={
          !draftEditInDrawer ? tooltipWhenEditInTable : tooltipWhenEditInDrawer
        }
      >
        <span
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            lineHeight: 0,
          }}
        >
          <Switch
            className="transactions-settings-drawer-switch"
            size="small"
            checked={!draftEditInDrawer}
            aria-label={
              !draftEditInDrawer
                ? tooltipWhenEditInTable
                : tooltipWhenEditInDrawer
            }
            onChange={(checked) => {
              onDraftEditInDrawerChange(!checked);
            }}
          />
        </span>
      </Tooltip>
    );
  }

  const {
    organisationId,
    handlePreferences,
    onPersistedEditInDrawerChange,
    tooltipWhenEditInTable,
    tooltipWhenEditInDrawer,
  } = props;

  const prefName = `${ENTITY_TRANSACTIONS_EDIT_IN_DRAWER_PREF}-${organisationId}`;
  const [editInDrawer, setEditInDrawer] = React.useState(true);

  React.useEffect(() => {
    if (!organisationId?.trim()) {
      setEditInDrawer(false);
      return;
    }
    const raw = handlePreferences(PreferenceActions.GET, { name: prefName });
    const allRaw = handlePreferences(PreferenceActions.GET, {
      name: ENTITY_TRANSACTIONS_EDIT_IN_DRAWER_PREF_ALL_ENTITIES_KEY,
    });
    if (raw === true || raw === false) {
      setEditInDrawer(raw === true);
      return;
    }
    if (allRaw === true || allRaw === false) {
      setEditInDrawer(allRaw === true);
      return;
    }
    // Default: open drawer unless user saved otherwise.
    setEditInDrawer(true);
  }, [organisationId, prefName, handlePreferences]);

  return (
    <Tooltip
      title={!editInDrawer ? tooltipWhenEditInTable : tooltipWhenEditInDrawer}
    >
      <span
        style={{ display: 'inline-flex', alignItems: 'center', lineHeight: 0 }}
      >
        <Switch
          className="transactions-settings-drawer-switch"
          size="small"
          checked={!editInDrawer}
          aria-label={
            !editInDrawer ? tooltipWhenEditInTable : tooltipWhenEditInDrawer
          }
          onChange={(checked) => {
            const nextEditInDrawer = !checked;
            setEditInDrawer(nextEditInDrawer);
            handlePreferences(PreferenceActions.SET, {
              name: prefName,
              value: nextEditInDrawer,
            });
            onPersistedEditInDrawerChange(nextEditInDrawer);
          }}
        />
      </span>
    </Tooltip>
  );
};

/** Edit in drawer ↔ table toggle row (customise drawer + review document drawer header). */
export const EntityTransactionsEditInDrawerToggleRow: React.FC<
  | {
      mode?: 'persisted';
      organisationId: string;
      handlePreferences: (
        action: PreferenceActions,
        preference: Preference
      ) => unknown;
      onPersistedEditInDrawerChange: (editInDrawer: boolean) => void;
      showHelpTooltip?: boolean;
    }
  | {
      mode: 'draft';
      draftEditInDrawer: boolean;
      onDraftEditInDrawerChange: (editInDrawer: boolean) => void;
      showHelpTooltip?: boolean;
    }
> = (props) => {
  const { t } = useTranslation();
  const showHelpTooltip = props.showHelpTooltip ?? false;

  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'flex-start',
        gap: 8,
        flexWrap: 'nowrap',
      }}
    >
      <span style={{ fontSize: 12, whiteSpace: 'nowrap', color: 'var(--color-text-dark, #042a2a)' }}>
        {t('organisation.edit-in-drawer')}
      </span>
      {props.mode === 'draft' ? (
        <EntityTransactionsEditInTablePrefSwitch
          draftMode
          draftEditInDrawer={props.draftEditInDrawer}
          onDraftEditInDrawerChange={props.onDraftEditInDrawerChange}
          tooltipWhenEditInTable={t('common.toggle-on-click-to-turn-off')}
          tooltipWhenEditInDrawer={t('common.toggle-off-click-to-turn-on')}
        />
      ) : (
        <EntityTransactionsEditInTablePrefSwitch
          organisationId={props.organisationId}
          handlePreferences={props.handlePreferences}
          onPersistedEditInDrawerChange={props.onPersistedEditInDrawerChange}
          tooltipWhenEditInTable={t('common.toggle-on-click-to-turn-off')}
          tooltipWhenEditInDrawer={t('common.toggle-off-click-to-turn-on')}
        />
      )}
      <span style={{ fontSize: 12, whiteSpace: 'nowrap', color: 'var(--color-text-dark, #042a2a)' }}>
        {t('organisation.edit-transactions-style-table')}
      </span>
      {showHelpTooltip ? (
        <QuestionMarkTooltip
          title={t('organisation.edit-transactions-in-table-help')}
        />
      ) : null}
    </div>
  );
};

/** Column + editing-style settings drawer: dual save (current vs all entities). */
export function TransactionsTabSettingsDrawerContent(props: {
  organisationId: string;
  handlePreferences: (
    action: PreferenceActions,
    preference: Preference
  ) => unknown;
  /** Writes multiple ENTITIES prefs in one request (avoids last-SET-wins races). */
  setEntityPreferencesBatch?: (updates: Preference[]) => void;
  initialEditInDrawer: boolean;
  customizableColumnsForDrawer: {
    key: string;
    title: string;
    fixed?: boolean | 'left' | 'right';
  }[];
  effectiveVisibleColumns: string[];
  lockedColumns: string[];
  columnOrder: string[] | undefined;
  alwaysFixedColumns: string[];
  optionalColumnKeys: string[];
  nonReorderableColumnKeys?: string[];
  /** Always shown — no hide checkbox; lock still available. */
  alwaysVisibleColumnKeys?: string[];
  /** When false (e.g. Group entity), hide edit-in-drawer vs table switch. */
  showEditInDrawerSetting: boolean;
  onApplyColumns: (config: TransactionsTabColumnsPersistConfig) => void;
  onApplyEditInDrawer: (editInDrawer: boolean) => void;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const {
    organisationId,
    handlePreferences,
    setEntityPreferencesBatch,
    initialEditInDrawer,
    customizableColumnsForDrawer,
    effectiveVisibleColumns,
    lockedColumns,
    columnOrder,
    alwaysFixedColumns,
    optionalColumnKeys,
    nonReorderableColumnKeys = [],
    alwaysVisibleColumnKeys = [],
    showEditInDrawerSetting,
    onApplyColumns,
    onApplyEditInDrawer,
    onClose,
  } = props;

  const [draftEditInDrawer, setDraftEditInDrawer] =
    React.useState(initialEditInDrawer);
  const baselineEditInDrawerRef = React.useRef(initialEditInDrawer);

  React.useEffect(() => {
    setDraftEditInDrawer(initialEditInDrawer);
    baselineEditInDrawerRef.current = initialEditInDrawer;
  }, [initialEditInDrawer]);

  const persistAndApply = React.useCallback(
    (
      scope: 'entity' | 'all_entities',
      config: TransactionsTabColumnsPersistConfig
    ) => {
      const colsKeyGlobal = TRANSACTIONS_TAB_COLUMNS_CONFIG_ALL_ENTITIES_KEY;
      const configWithEdit: TransactionsTabColumnsPersistConfig = {
        ...config,
        editInDrawer: draftEditInDrawer,
      };

      const preferenceUpdates: Preference[] = [];
      if (scope === 'all_entities') {
        preferenceUpdates.push({
          name: colsKeyGlobal,
          value: configWithEdit,
        });
        if (organisationId?.trim()) {
          preferenceUpdates.push({
            name: transactionsTabColumnsConfigKeyForOrg(organisationId),
            value: configWithEdit,
          });
        }
      } else if (organisationId?.trim()) {
        preferenceUpdates.push({
          name: transactionsTabColumnsConfigKeyForOrg(organisationId),
          value: configWithEdit,
        });
      }
      if (preferenceUpdates.length > 0) {
        if (setEntityPreferencesBatch) {
          setEntityPreferencesBatch(preferenceUpdates);
        } else {
          preferenceUpdates.forEach((preference) =>
            handlePreferences(PreferenceActions.SET, preference)
          );
        }
      }

      onApplyColumns(configWithEdit);
      onApplyEditInDrawer(draftEditInDrawer);
      baselineEditInDrawerRef.current = draftEditInDrawer;
    },
    [
      organisationId,
      handlePreferences,
      setEntityPreferencesBatch,
      draftEditInDrawer,
      onApplyColumns,
      onApplyEditInDrawer,
    ]
  );

  const dualEntityScopeSave = React.useMemo(
    () => ({
      saveForEntityLabel: t(
        'organisation.transactions-settings-save-this-entity'
      ),
      saveForAllEntitiesLabel: t(
        'organisation.transactions-settings-save-all-entities'
      ),
      onSaveForEntity: (config: TransactionsTabColumnsPersistConfig) => {
        persistAndApply('entity', config);
      },
      onSaveForAllEntities: (config: TransactionsTabColumnsPersistConfig) => {
        persistAndApply('all_entities', config);
      },
      successDescriptionForEntity: t(
        'organisation.transactions-settings-saved-entity'
      ),
      successDescriptionForAllEntities: t(
        'organisation.transactions-settings-saved-all-entities'
      ),
    }),
    [t, persistAndApply]
  );

  const settingsTopSection = showEditInDrawerSetting ? (
    <EntityTransactionsEditInDrawerToggleRow
      mode="draft"
      draftEditInDrawer={draftEditInDrawer}
      onDraftEditInDrawerChange={setDraftEditInDrawer}
      showHelpTooltip
    />
  ) : null;

  return (
    <ColumnCustomizationDrawer
      columns={customizableColumnsForDrawer}
      visibleColumns={effectiveVisibleColumns}
      lockedColumns={lockedColumns}
      columnOrder={columnOrder}
      alwaysFixedColumns={alwaysFixedColumns}
      optionalColumnKeys={optionalColumnKeys}
      nonReorderableColumnKeys={nonReorderableColumnKeys}
      alwaysVisibleColumnKeys={alwaysVisibleColumnKeys}
      topSectionHeading={
        showEditInDrawerSetting
          ? t('organisation.transactions-settings-section-editing-title')
          : undefined
      }
      topSectionDescription={
        showEditInDrawerSetting
          ? t('organisation.transactions-settings-section-editing-description')
          : undefined
      }
      topSection={settingsTopSection ?? undefined}
      columnListHeading={t(
        'organisation.transactions-settings-section-columns-title'
      )}
      columnListDescription={t(
        'organisation.transactions-settings-section-columns-description'
      )}
      dualEntityScopeSave={dualEntityScopeSave}
      additionalHasChanges={
        showEditInDrawerSetting &&
        draftEditInDrawer !== baselineEditInDrawerRef.current
      }
      onClose={onClose}
    />
  );
}
